import { normalizeReprojection } from './cameraReprojection';
import {
  normalizeLiveMeta,
  parsePoseBuffer,
  resolvePoseUrl,
} from './trialLiveLayout';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function isServerUrl(url, server) {
  if (!server) return false;
  if (url === server) return true;
  const prefix = server.endsWith('/') ? server : `${server}/`;
  return url.indexOf(prefix) === 0;
}

function requestHeaders(url) {
  const server = window.SERVER_URL || '';
  const absolute = /^https?:\/\//i.test(url);
  if (absolute && !isServerUrl(url, server)) return {};
  return authHeaders();
}

/**
 * 2D joint tracks for the camera overlay. A presigned object URL must not
 * receive the session bearer; S3 rejects that header.
 */
export async function fetchReprojection(reprojectionUrl) {
  const url = resolvePoseUrl(reprojectionUrl, window.SERVER_URL);
  const response = await fetch(url, { headers: requestHeaders(url) });
  if (!response.ok) {
    throw new Error(`reprojection HTTP ${response.status}`);
  }
  return normalizeReprojection(await response.json());
}

/**
 * Load the per-trial live stage: meta plus the float32 pose buffer.
 * Camera videos stay on the <video> elements; this does not fetch a stitch.
 */
export async function fetchLiveLayout(trialId) {
  const headers = authHeaders();
  const metaRes = await fetch(
    `${window.SERVER_URL}/a/live-layout?trial=${encodeURIComponent(trialId)}`,
    { headers }
  );
  if (!metaRes.ok) {
    throw new Error(`live layout HTTP ${metaRes.status}`);
  }
  const raw = await metaRes.json();
  const meta = normalizeLiveMeta(raw);
  const poseUrl = resolvePoseUrl(meta.poseUrl, window.SERVER_URL);
  const poseRes = await fetch(poseUrl, { headers: requestHeaders(poseUrl) });
  if (!poseRes.ok) {
    throw new Error(`pose HTTP ${poseRes.status}`);
  }
  const buffer = await poseRes.arrayBuffer();
  const pose = parsePoseBuffer(buffer, meta.frameCount, meta.jointNames.length);
  let reprojection = null;
  if (meta.reprojectionUrl) {
    try {
      reprojection = await fetchReprojection(meta.reprojectionUrl);
    } catch (err) {
      console.error('Reprojection overlay unavailable:', err);
    }
  }
  return { meta, pose, reprojection };
}

/** Plain-text URL of `{trial}_combined.mp4`, the player used before live pose. */
export async function fetchCombinedVideoSrc(trialId) {
  const response = await fetch(
    `${window.SERVER_URL}/a/video?trial=${encodeURIComponent(trialId)}`,
    { headers: authHeaders() }
  );
  if (!response.ok) {
    throw new Error(`combined video HTTP ${response.status}`);
  }
  const videoUrl = String(await response.text()).trim();
  if (!videoUrl) {
    throw new Error('combined video URL empty');
  }
  return videoUrl;
}

/**
 * Live pose when the trial has one. Otherwise the combined video, so older
 * trials keep the previous player.
 */
export async function fetchReviewPlayback(trialId) {
  try {
    const layout = await fetchLiveLayout(trialId);
    return { mode: 'live', layout, videoSrc: null };
  } catch (liveErr) {
    try {
      const videoSrc = await fetchCombinedVideoSrc(trialId);
      return { mode: 'combined', layout: null, videoSrc };
    } catch (videoErr) {
      const error = new Error('review playback unavailable');
      error.cause = videoErr;
      error.liveError = liveErr;
      throw error;
    }
  }
}
